// src/modules/clients/pages/AddClientPage.tsx
import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Assignment as TicketIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import api from "../../../services/api";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";

type FormState = {
  fullName: string;
  email: string;
  phone: string;
  address: string;
};

type Errors = Partial<Record<keyof FormState, string>>;

export default function AddClientPage() {
  useProtectedRoute();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>({
    fullName: "",
    email: "",
    phone: "",
    address: "",
  });
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState<"ticket" | "profile" | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const setField =
    (name: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((f) => ({ ...f, [name]: e.target.value }));
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

  const validate = (): boolean => {
    const e: Errors = {};
    if (!form.fullName.trim()) e.fullName = "Full name is required";
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email";
    if (form.phone && form.phone.replace(/\D/g, "").length < 6) e.phone = "Enter a valid phone";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (intent: "ticket" | "profile") => {
    if (!validate()) return;
    setSubmitting(intent);
    setServerError(null);
    try {
      const res = await api.post("/clients", form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const created = res.data; // {_id,...}

      if (intent === "ticket") {
        navigate(`/tickets/create?clientId=${created._id}`, {
          state: { clientId: created._id, client: created, justCreatedClient: true },
          replace: true,
        });
      } else {
        navigate(`/clients/${created._id}/profile`, { replace: true });
      }
    } catch (err: any) {
      setServerError(
        err?.response?.data?.message ||
          (err?.response?.status === 401 ? "Not authenticated. Please login." : "Failed to add client.")
      );
      setSubmitting(null);
    }
  };

  const onSubmitForm = (e: React.FormEvent) => {
    e.preventDefault();
    submit("ticket");
  };

  const canSubmit = !!form.fullName.trim();

  return (
    <Box
      sx={{
        background: (t) =>
          t.palette.mode === "light"
            ? `linear-gradient(135deg, ${t.palette.primary.light}0d, ${t.palette.background.default})`
            : undefined,
        minHeight: "100%",
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="md">
        {/* Top row: back link */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Button
            component={RouterLink}
            to="/clients"
            startIcon={<ArrowBackIcon />}
            variant="text"
            sx={{ fontWeight: 700 }}
          >
            Back to Clients
          </Button>
        </Stack>

        {/* 🔷 Hero header (matches other pages) */}
        <Box
          sx={(t) => ({
            mb: 3,
            px: { xs: 2, md: 3 },
            py: { xs: 2, md: 3 },
            borderRadius: 3,
            color: t.palette.getContrastText(t.palette.primary.main),
            background: `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 75%)`,
            boxShadow: `0 6px 18px ${t.palette.primary.main}33`,
            display: "flex",
            alignItems: "center",
            gap: 2,
          })}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              minWidth: 42,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              background: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.35)",
            }}
          >
            <PersonIcon sx={{ color: "white" }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h5"
              fontWeight={900}
              sx={{ lineHeight: 1, color: "common.white" }}
              noWrap
            >
              Add New Client
            </Typography>
            <Typography
              variant="body2"
              sx={{ opacity: 0.9, color: "common.white" }}
              noWrap
            >
              Capture contact details, then jump straight into creating a ticket.
            </Typography>
          </Box>
        </Box>

        {serverError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {serverError}
          </Alert>
        )}

        {/* Form card */}
        <Card
          elevation={1}
          sx={{
            borderRadius: 3,
            border: (t) => `1px solid ${t.palette.divider}`,
          }}
        >
          <CardContent>
            <Box component="form" noValidate onSubmit={onSubmitForm}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    name="fullName"
                    label="Full Name"
                    value={form.fullName}
                    onChange={setField("fullName")}
                    error={!!errors.fullName}
                    helperText={errors.fullName}
                    autoFocus
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="email"
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={setField("email")}
                    error={!!errors.email}
                    helperText={errors.email || "Optional"}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="phone"
                    label="Phone"
                    value={form.phone}
                    onChange={setField("phone")}
                    error={!!errors.phone}
                    helperText={errors.phone || "Optional"}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      inputMode: "tel",
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="address"
                    label="Address"
                    value={form.address}
                    onChange={setField("address")}
                    helperText="Optional"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HomeIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 0.5 }} />
                </Grid>

                <Grid item xs={12}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    justifyContent="flex-end"
                    alignItems={{ xs: "stretch", sm: "center" }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() =>
                        setForm({ fullName: "", email: "", phone: "", address: "" })
                      }
                      disabled={submitting !== null}
                      sx={{ borderRadius: 2 }}
                    >
                      Clear
                    </Button>

                    {/* Save only -> go to profile */}
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={
                        submitting === "profile" ? (
                          <CircularProgress size={18} />
                        ) : (
                          <SaveIcon />
                        )
                      }
                      onClick={() => submit("profile")}
                      disabled={!canSubmit || submitting !== null}
                      sx={{ borderRadius: 2 }}
                    >
                      Save
                    </Button>

                    {/* Primary: Save & Create Ticket */}
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      startIcon={
                        submitting === "ticket" ? (
                          <CircularProgress size={18} />
                        ) : (
                          <TicketIcon />
                        )
                      }
                      disabled={!canSubmit || submitting !== null}
                      sx={{ borderRadius: 2, fontWeight: 800 }}
                    >
                      Save & Create Ticket
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>

        {/* Subtle page hint */}
        <Box
          sx={{
            mt: 3,
            px: 2,
            py: 1.5,
            borderRadius: 2,
            border: (t) => `1px dashed ${t.palette.divider}`,
            background: (t) =>
              t.palette.mode === "light"
                ? `${t.palette.primary.light}08`
                : `${t.palette.primary.dark}14`,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Tip: You can always edit client details later from the client profile page.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

